/**
 * paginateRows — pure pagination windowing for SimpleTable.
 *
 * Extracted from `useSimpleTableData` so the page-windowing decision is
 * unit-testable in isolation (it has no hook / context dependencies).
 *
 * ## Why this exists
 *
 * SimpleTable is fed by two DIFFERENT data-source contracts:
 *
 *  1. True server pagination (e.g. the deals GraphQL cursor query) returns ONLY
 *     the current page's rows (`first:pageSize, after:cursor`) plus a full
 *     `totalCount` for the footer.
 *  2. Client-materialized sources (the deals RPC-filter branch, the FTS-search
 *     branch) return the ENTIRE result set but ALSO report `totalItems` = the
 *     full count.
 *
 * The old logic treated "`totalItems` is defined" as "the server already
 * paginated, render the rows as-is." For contract #2 that rendered the whole
 * result set on every page — the "11 results, click next, see all 11" bug.
 *
 * The disambiguator is the one fact the table can observe directly: a
 * server-paginated source hands back AT MOST one page of rows; a
 * client-materialized source hands back more. So when we hold more than a page,
 * we window client-side regardless of whether `totalItems` was reported.
 */
export interface PaginateRowsParams<T> {
  /** Rows currently held by the table for the active view. */
  rows: T[];
  /** Zero-based current page index. */
  currentPage: number;
  /** Rows per page. */
  pageSize: number;
  /**
   * Total row count across all pages as reported by the data source (drives the
   * footer "X of Y"). `undefined` means the source is purely client-paginated
   * and never reports a server total.
   */
  totalItems?: number;
  /**
   * True when the caller intentionally renders the full accumulated row set and
   * windowing would corrupt it — i.e. infinite-scroll mode (rows accumulate as
   * the user scrolls) or active grouping (rows include group-header entries).
   */
  disableClientSlice?: boolean;
}

/**
 * Returns the rows that should actually render for the current page.
 *
 * @see PaginateRowsParams for the contract behind each branch.
 */
export function paginateRows<T>({
  rows,
  currentPage,
  pageSize,
  totalItems,
  disableClientSlice,
}: PaginateRowsParams<T>): T[] {
  // Infinite scroll / grouping render the full set on purpose — never window it.
  if (disableClientSlice) return rows;

  // Defensive: a non-positive page size can't define a window; render as-is
  // rather than producing an empty slice.
  if (pageSize <= 0) return rows;

  // Server already returned exactly one page: it handed back no more than a
  // page-worth of rows, so it pre-sliced. Trust it and render as-is.
  if (totalItems !== undefined && rows.length <= pageSize) return rows;

  // Otherwise we hold the FULL result set — either purely client-side
  // pagination (no `totalItems`), or a source that advertised a full
  // `totalItems` count yet returned every matching row (RPC filter / FTS
  // search). Window it to the current page so page 2 shows only its slice,
  // never the entire result set.
  const start = currentPage * pageSize;
  return rows.slice(start, start + pageSize);
}
